import { mdiChevronDown, mdiChevronRight, mdiChevronUp } from '@mdi/js'
import Icon from '@mdi/react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box } from '@/components/ui/box'
import { Button } from '@/components/ui/button'
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@/components/ui/menu'
import { Text } from '@/components/ui/typography'
import useColorModeValue from '@/hooks/useColorModeValue'
import { toggleUploadMobileDrawer } from '@/store/gigiChat/ChatAtom'
import { IConversationSettings, IGroupedData, ISnippetSourceBundle, TProcessingStrategy } from '@/types/gigiChat/ConversationSettings'

const MobileSettingsOptions = ({
    chatSettings,
    options,
    onSettingsChange,
    defaultBundle,
}: {
    chatSettings: IConversationSettings | null
    options: ISnippetSourceBundle[]
    defaultBundle: string
    onSettingsChange: (processingStrategy: TProcessingStrategy, key: string, checkValue: string | number) => void
}) => {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const getWritingValue = () => {
        const temperature = chatSettings?.processingStrategyWriting.temperature || 0
        if (temperature < 0.5) {
            return 'Predictive'
        } else if (temperature >= 0.5 && temperature < 1) {
            return 'Balanced'
        } else if (temperature === 1) {
            return 'Creative'
        }
    }
    const writingDropdownTextColor = useColorModeValue('var(--chakra-colors-gray-450)', 'var(--chakra-colors-gray-600)')
    const textColor = useColorModeValue('rgba(84, 79, 64, 0.7)', 'var(--chakra-colors-gray-500)')

    const getDocumentsButton = () => {
        return (
            <MenuRoot>
                <MenuTrigger asChild>
                    <Button variant="mainSettings" paddingRight="4px" paddingLeft="8px" onClick={toggleUploadMobileDrawer}>
                        <Box display="flex" alignItems="center">
                            <Text marginRight="4px">Manage Documents</Text>
                            <Icon path={mdiChevronRight} size="24px" />
                        </Box>
                    </Button>
                </MenuTrigger>
            </MenuRoot>
        )
    }

    const displayLabelData = (qaOptions: ISnippetSourceBundle[]) => {
        return qaOptions.map((option) => (
            <MenuItem
                value={option.bundleName}
                key={option.bundleName}
                onClick={() => onSettingsChange('QA', option.bundleName, option.bundleName)}
            >
                <Text fontSize="14px" letterSpacing="0.25px">
                    {option.bundleName}
                </Text>
            </MenuItem>
        ))
    }

    const generateQaSettings = () => {
        const sortedGroupedData: IGroupedData = options
            .sort((a: { label: string; orderNumber: number }, b: { label: string; orderNumber: number }) => {
                return a.orderNumber - b.orderNumber
            })
            .reduce((acc: IGroupedData, item) => {
                const label = item.label
                if (!acc[label]) {
                    acc[label] = []
                }
                acc[label].push(item)
                return acc
            }, {})

        const labelsData = Object.keys(sortedGroupedData)

        return (
            <Box pt="8px" pr="8px" pl="8px">
                {labelsData.map((label) => (
                    <React.Fragment key={label}>
                        {labelsData.length > 1 && (
                            <Box key={label} mb="4px">
                                <Text color={textColor} fontSize="12px" letterSpacing="0.5px">
                                    {label}
                                </Text>
                            </Box>
                        )}
                        <Box display="grid" mb="4px">
                            {displayLabelData(sortedGroupedData[label])}
                        </Box>
                    </React.Fragment>
                ))}
            </Box>
        )
    }

    const geTranslateButton = () => {
        return (
            <MenuRoot>
                <MenuTrigger asChild>
                    <Button variant="mainSettings" paddingRight="4px" paddingLeft="8px" onClick={toggleUploadMobileDrawer}>
                        <Box display="flex" alignItems="center">
                            <Text marginRight="4px">Manage Documents</Text>
                            <Icon path={mdiChevronRight} size="24px" />
                        </Box>
                    </Button>
                </MenuTrigger>
            </MenuRoot>
        )
    }

    if (chatSettings?.processingStrategy === 'Translation') {
        return geTranslateButton()
    }

    return (
        <>
            {chatSettings?.processingStrategy === 'DocAnalysis' ? (
                getDocumentsButton()
            ) : (
                <Box>
                    <MenuRoot onOpenChange={(details) => setIsOpen(details.open)} variant="settings" open={isOpen}>
                        <MenuTrigger asChild>
                            <Button variant="mainSettings" paddingRight="4px" paddingLeft="8px" onClick={() => setIsOpen(!isOpen)}>
                                <Box display="flex" alignItems="center">
                                    <Text marginRight="4px">
                                        {chatSettings?.processingStrategy === 'QA' && defaultBundle}
                                        {chatSettings?.processingStrategy === 'Writing' && getWritingValue()}
                                    </Text>
                                    <Icon path={isOpen ? mdiChevronUp : mdiChevronDown} size="24px" />
                                </Box>
                            </Button>
                        </MenuTrigger>
                        <MenuContent>
                            {chatSettings?.processingStrategy === 'QA' && generateQaSettings()}
                            {chatSettings?.processingStrategy === 'Writing' && (
                                <Box>
                                    <Text fontSize="12px" maxW="165px" ml="12px" mt="4px" color={writingDropdownTextColor}>
                                        {t('chat.settings.dropdown.description')}
                                    </Text>
                                    <MenuItem
                                        onClick={() => onSettingsChange('Writing', 'temperature', 1)}
                                        mb="12px"
                                        value={t('chat.settings.dropdown.creative')}
                                    >
                                        <Text fontSize="14px" letterSpacing="0.25px">
                                            {t('chat.settings.dropdown.creative')}
                                        </Text>
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => onSettingsChange('Writing', 'temperature', 0.5)}
                                        mb="12px"
                                        value={t('chat.settings.dropdown.balanced')}
                                    >
                                        <Text fontSize="14px" letterSpacing="0.25px">
                                            {t('chat.settings.dropdown.balanced')}
                                        </Text>
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => onSettingsChange('Writing', 'temperature', 0)}
                                        mb="12px"
                                        value={t('chat.settings.dropdown.predictable')}
                                    >
                                        <Text fontSize="14px" letterSpacing="0.25px">
                                            {t('chat.settings.dropdown.predictable')}
                                        </Text>
                                    </MenuItem>
                                </Box>
                            )}
                        </MenuContent>
                    </MenuRoot>
                </Box>
            )}
        </>
    )
}

export default MobileSettingsOptions
